<template>
	<div class="current-trip">
		<div class="header">
			<div
				class="back-btn"
				@click="goBack"
			>
				<Icon
					name="material-symbols:arrow-left-alt-rounded"
					color="#FFFFFF"
				/>
				<TypographyKTypographyBody5
					color="white"
					font-type="medium"
					class="text"
				>
					назад
				</TypographyKTypographyBody5>
			</div>
		</div>
		<div class="content">
			<div class="content-wrapper">
				<div class="title-wrapper">
					<div class="title">Рейс № {{ currentTrip?.number }}</div>
					<div class="count">
						<Icon
							name="solar:box-minimalistic-linear"
							color="#fff"
							class="cargo-count-icon"
						/>
						{{ currentTrip?.countCargos }}
					</div>
				</div>
				<div class="cities">
					<Icon
						name="mynaui:location"
						color="#fff"
						class="cargo-town"
					/>
					<div
						v-for="(city, index) in currentTrip?.intermediateCityNames"
						:key="city"
						class="city"
					>
						{{ city }}
						<Icon
							v-if="index + 1 !== currentTrip?.intermediateCityNames?.length"
							name="uil:arrow-right"
							class="arrow-icon"
							color="#fff"
						/>
					</div>
				</div>
				<div class="filter-list">
					<div
						v-for="filter in filterList"
						:key="filter.id"
						class="cargo"
						:class="{ cargo__active: isFilterActive(filter) }"
						:style="{
							color: 'var(--black)',
							borderColor: isFilterActive(filter) ? filter?.backgroundColor : 'var(--black)'
						}"
						@click="setFilter(filter.filter)"
					>
						<div class="cargo__icon-wrapper">
							<UiIcon
								:name="filter.variableName"
								folder="cargos"
								:color="isFilterActive(filter) ? filter?.backgroundColor : 'var(--black)'"
								v-if="filter.variableName"
							/>
							<TypographyKTypographyBody5
								:color="filter?.backgroundColor"
								font-type="semi-bold"
							>
								({{ filter?.count || 0 }})
							</TypographyKTypographyBody5>
						</div>

						<TypographyKTypographyBody4
							class="cargo__title"
							:color="isFilterActive(filter) ? filter?.backgroundColor : 'var(--black)'"
						>
							{{ filter?.name }}
						</TypographyKTypographyBody4>
					</div>
				</div>
				<div
					v-if="activeFilter?.subBtns"
					class="btns-additional"
				>
					<button
						v-for="subBtn in Object.values(activeFilter.subBtns)"
						:key="subBtn.filter"
						type="button"
						class="btns-additional__button"
						:class="{ 'btns-additional__button--active': cargoTypeResults === subBtn.filter }"
						@click="setFilter(subBtn.filter)"
					>
						<Icon
							:name="subBtn.icon"
							class="btns-additional__icon"
						/>
						<span>{{ subBtn.title }}</span>
					</button>
				</div>
				<div
					class="cargos-by-trip"
					v-if="!loading.getDetailsByCargoInTrip && currentTripList.length > 0"
				>
					<div class="cargos-list-wrapper">
						<div
							v-for="[groupName, cargos] in currentTripList"
							:key="groupName"
							class="cargo-group"
						>
							<div class="cargo-group__header">
								<div class="cargo-group__title">
									<Icon
										name="lucide:warehouse"
										color="#fff"
										class="cargo-group__icon"
									/>
									<TypographyKTypographyBody4 font-type="bold">{{ groupName }}</TypographyKTypographyBody4>
									<span class="cargo-group__count">{{ cargos.length }}</span>
								</div>
								<button
									class="cargo-group__toggle"
									type="button"
									:aria-expanded="!collapsedGroups.has(groupName)"
									@click="toggleGroup(groupName)"
								>
									<div class="toggle">
										{{ collapsedGroups.has(groupName) ? 'развернуть' : 'свернуть' }}
									</div>

									<Icon
										name="material-symbols:keyboard-arrow-down-rounded"
										color="#fff"
										:class="{ 'cargo-group__arrow--collapsed': collapsedGroups.has(groupName) }"
									/>
								</button>
							</div>
							<div
								v-if="!collapsedGroups.has(groupName)"
								class="cargo-group__cards"
							>
								<PagesTruckerTripCargoCard
									v-for="item in cargos"
									:key="item.id"
									:cargo="item"
									@open="openCargoModal"
								/>
							</div>
						</div>
					</div>
				</div>
				<div
					v-else-if="loading.getDetailsByCargoInTrip"
					class="loader"
				>
					<UiLoader
						:radius="20"
						:circle-width="2"
						:color="'#fff'"
					/>
				</div>
				<div
					v-else-if="currentTripList.length === 0"
					class="empty-cargos"
				>
					<TypographyKTypographyBody3 color="#fff">Грузов нет</TypographyKTypographyBody3>
					<UiButton
						:color="ButtonType.primary"
						@click="getCargosByTrip()"
					>
						Перезагрузить
					</UiButton>
				</div>
			</div>
		</div>
		<PagesTruckerCargoDetailsModal
			v-if="isCargoModalOpen && selectedCargo"
			v-model="isCargoModalOpen"
			:cargo="selectedCargo"
			:details="selectedCargoDetails"
			:is-loading="loading.cargoDetails"
			:error="cargoDetailsError"
			@issue="openCargoIssueModal"
			@retry="reloadCargoDetails"
		/>
		<PagesTruckerCargoIssueModal
			v-if="isCargoIssueModalOpen && selectedCargo && selectedCargoDetails"
			v-model="isCargoIssueModalOpen"
			:cargo-id="selectedCargo.id"
			:cargo-number="selectedCargo.number"
			:currency-ticker="selectedCargoDetails.currencyTicker"
			:selectedCargoDetails="selectedCargoDetails"
			:is-submitting="loading.submitCargoIssue"
			@cancel="closeCargoIssueModal"
			@issue="submitCargoIssue"
		/>
	</div>
</template>

<script setup lang="ts">
	import { useLongHaulTripStore } from '~/store/longHaulTrip.store';
	import { ButtonType } from '~~/shared/interfaces/ui';
	import type { ICargoByTripItem, ICargoInTripDetails, ITrip, TCargoTypeResults } from '~~/shared/interfaces/truck';

	type TTripFilter = {
		id: number;
		name: string;
		filter: TCargoTypeResults;
		variableName: string;
		backgroundColor: string;
		subBtns?: {
			Delivery: { title: string; filter: TCargoTypeResults; icon: string };
			Delivered: { title: string; filter: TCargoTypeResults; icon: string };
		};
	};

	const router = useRouter();
	const goBack = () => {
		router.push('/trucker');
	};

	const longHaulTripStore = useLongHaulTripStore();
	const currentTrip = ref<ITrip | null>(null);
	const defaultCargoFilter: TCargoTypeResults = 'Delivery';
	const loading = reactive({
		getDetailsByCargoInTrip: false,
		cargoDetails: false,
		submitCargoIssue: false
	});
	const cargoTypeResults = ref<TCargoTypeResults>(defaultCargoFilter);
	const getTripId = computed(() => router.currentRoute?.value.params?.tripId as string);
	const currentTripList = ref<[string, ICargoByTripItem[]][]>([]);
	const collapsedGroups = ref(new Set<string>());
	const isCargoModalOpen = ref(false);
	const selectedCargo = ref<ICargoByTripItem | null>(null);
	const selectedCargoDetails = ref<ICargoInTripDetails | null>(null);
	const cargoDetailsError = ref('');
	const isCargoIssueModalOpen = ref(false);
	let cargoDetailsRequestId = 0;

	const toggleGroup = (groupName: string) => {
		if (collapsedGroups.value.has(groupName)) {
			collapsedGroups.value.delete(groupName);
		} else {
			collapsedGroups.value.add(groupName);
		}
	};

	const filterList: Ref<TTripFilter[]> = computed(() => {
		return [
			{
				id: 1,
				name: 'К выдаче',
				filter: 'Delivery',
				variableName: 'newCargos',
				backgroundColor: '#FF8C00',
				count: (currentTrip.value?.countDeliveryCargos || 0) + (currentTrip.value?.countDeliveredCargos || 0),
				subBtns: {
					Delivery: {
						title: 'К выдаче',
						filter: 'Delivery',
						icon: 'streamline:shipping-transfer-cart-package-box-fulfillment-cart-warehouse-shipping-delivery'
					},
					Delivered: {
						title: 'Выдано',
						filter: 'Delivered',
						icon: 'icon-park-twotone:delivery'
					}
				}
			},
			{
				id: 2,
				name: 'Все грузы',
				filter: 'All',
				variableName: 'pickedupCargos',
				backgroundColor: '#4180FF',
				count: currentTrip.value?.countCargos || 0
			}
		];
	});

	const activeFilter = computed(() =>
		filterList.value?.find((filter) => {
			if (filter.filter === cargoTypeResults.value) return true;
			return filter.subBtns
				? Object.values(filter.subBtns).some((subBtn) => subBtn.filter === cargoTypeResults.value)
				: false;
		})
	);

	const isFilterActive = (filter: (typeof filterList)[number]) =>
		filter.filter === cargoTypeResults.value ||
		(Boolean(filter.subBtns) &&
			Object.values(filter.subBtns).some((subBtn) => subBtn.filter === cargoTypeResults.value));

	const setFilter = (filter: TCargoTypeResults) => {
		if (cargoTypeResults.value === filter) return;
		cargoTypeResults.value = filter;

		getCargosByTrip();
	};

	const loadCargoDetails = async (cargo: ICargoByTripItem) => {
		const requestId = ++cargoDetailsRequestId;
		selectedCargoDetails.value = null;
		cargoDetailsError.value = '';
		loading.cargoDetails = true;

		try {
			const response = await longHaulTripStore.getDetailsByCargoInTrip({
				cargoId: cargo.id,
				tripId: getTripId.value
			});

			if (requestId !== cargoDetailsRequestId) return;

			if (response.status === 'success' && response.data?.isSuccessful && response.data.data) {
				selectedCargoDetails.value = response.data.data;
			} else {
				cargoDetailsError.value = 'Не удалось загрузить информацию о грузе';
			}
		} finally {
			if (requestId === cargoDetailsRequestId) {
				loading.cargoDetails = false;
			}
		}
	};

	const openCargoModal = async (cargo: ICargoByTripItem) => {
		selectedCargo.value = cargo;
		isCargoModalOpen.value = true;
		await loadCargoDetails(cargo);
	};

	const reloadCargoDetails = async () => {
		if (!selectedCargo.value || loading.cargoDetails) return;
		await loadCargoDetails(selectedCargo.value);
	};

	const openCargoIssueModal = () => {
		isCargoModalOpen.value = false;
		isCargoIssueModalOpen.value = true;
	};

	const closeCargoIssueModal = () => {
		isCargoIssueModalOpen.value = false;
		isCargoModalOpen.value = true;
	};

	const submitCargoIssue = async (payload: { paymentAmount: number | null }) => {
		if (!selectedCargo.value || loading.submitCargoIssue) return;

		loading.submitCargoIssue = true;
		try {
			const response = await longHaulTripStore.setDeliveredCargo({
				cargoId: selectedCargo.value.id,
				tripId: getTripId.value,
				paymentAmount: payload.paymentAmount
			});

			if (response.status !== 'success') return;

			closeCargoIssueModal();
			cargoTypeResults.value = 'Delivery';
			isCargoModalOpen.value = false;
			await getCargosByTrip(false);
		} finally {
			loading.submitCargoIssue = false;
		}
	};

	const getCargosByTrip = async (isNeedLoad = true) => {
		if (isNeedLoad) loading.getDetailsByCargoInTrip = true;

		const response = await longHaulTripStore.getCargosByTrip({
			cargoTypeResults: cargoTypeResults.value,
			tripId: getTripId.value || ''
		});

		if (response.status === 'success') {
			currentTripList.value = Object.entries(response.data?.data || {});
			collapsedGroups.value.clear();
		}

		loading.getDetailsByCargoInTrip = false;
		return response;
	};

	const getTripById = async () => {
		const response = await longHaulTripStore.getTripById(getTripId.value || '');
		currentTrip.value =
			response.status === 'success' && response.data?.isSuccessful && response.data.data ? response.data.data : null;
	};

	const stopPolling = () => {
		longHaulTripStore.cancelRequest('getCargosByTrip');
		longHaulTripStore.cancelRequest(`getTripById:${getTripId.value}`);
	};

	onMounted(() => {
		void getTripById();
		getCargosByTrip();
	});

	onUnmounted(() => {
		stopPolling();
	});

	onBeforeRouteLeave(stopPolling);
</script>

<style scoped lang="scss">
	:deep(.cargo-count-icon path) {
		stroke-width: 2;
	}
	.filter-list {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 8px;
		margin-top: 6px;

		.cargo {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			gap: 4px;
			background-color: var(--white);
			border-radius: 16px;
			padding: 2px 4px;
			height: 64px;
			cursor: pointer;

			&__icon-wrapper {
				display: flex;
				align-items: center;
				gap: 6px;
			}

			&__title {
				display: flex;
				white-space: nowrap;
				gap: 5px;
			}

			.count {
			}

			&__active {
				border: 1.8px solid;
				box-shadow: inset 0 0 0 2.5px #264688;
			}
		}
	}

	.btns-additional {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 10px;
		margin: 12px 0px 0;

		&__button {
			display: flex;
			min-width: 0;
			height: 30px;
			align-items: center;
			justify-content: center;
			gap: 4px;
			border: 0;
			border-radius: 8px;
			padding: 0 2px;
			color: #d7e2f7;
			font: inherit;
			font-size: 12px;
			background-blend-mode: overlay;
			background: linear-gradient(260.49deg, rgba(255, 255, 255, 0) -0.38%, rgba(255, 255, 255, 0.08) 153.72%);
			background: #37547b9e;

			cursor: pointer;

			&--active {
				background: #37547b9e;
				background-blend-mode: overlay;
				background: linear-gradient(260.49deg, rgba(255, 255, 255, 0) -0.38%, rgba(255, 255, 255, 0.08) 153.72%);
				background: #415c94;
				color: #fff;
			}
		}

		&__icon {
			width: 20px;
			height: 20px;
			flex: 0 0 20px;
		}
	}

	.content {
		flex: 1;
		min-height: 0;
	}
	.content-wrapper {
		display: flex;
		flex-direction: column;
		height: 100%;
	}
	.cargos-list-wrapper {
		display: flex;
		flex-direction: column;
		gap: 12px;
		padding: 12px 0 24px;
	}
	.cargo-group {
		&__header,
		&__title,
		&__toggle {
			display: flex;
			align-items: center;
		}

		&__header {
			justify-content: space-between;
			gap: 8px;
			margin-bottom: 6px;
			color: #fff;
		}

		&__title {
			min-width: 0;
			gap: 8px;
		}

		&__icon {
			width: 24px;
			height: 24px;
			flex: 0 0 24px;
		}

		&__count {
			display: inline-flex;
			width: 18px;
			height: 18px;
			flex: 0 0 18px;
			align-items: center;
			justify-content: center;
			border-radius: 50%;
			color: #fff;
			font-size: 11px;
			background-color: #ff8c00;
			font-weight: 700;
		}

		&__toggle {
			gap: 1px;
			padding: 0;
			border: 0;
			color: #fff;
			font-size: 13px;
			font-weight: 500;
			text-decoration: underline dotted;
			background: transparent;
			cursor: pointer;
			white-space: nowrap;
		}

		&__arrow--collapsed {
			transform: rotate(180deg);
		}

		&__cards {
			display: flex;
			flex-direction: column;
			gap: 12px;
		}
	}
	.loader {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
	}
	.empty-cargos {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		gap: 12px;
		flex: 1;
		min-height: 0;
	}
	.title-wrapper {
		margin-top: 10px;
		display: flex;
		justify-content: space-between;
		gap: 10px;
		align-items: center;
		color: #fff;

		.title {
			font-size: 16px;
			font-weight: 700;
		}
	}
	.cities {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 5px;

		&:deep(.cargo-town path) {
			stroke-width: 2;
		}

		.city {
			display: flex;
			align-items: center;
			height: 13px;
			gap: 4px;
			color: #ffffff;
			font-size: 12px;
			font-weight: 500;
		}
	}
	.current-trip {
		height: 100%;
		display: flex;
		flex-direction: column;
		margin-top: 14px;

		.header {
			display: flex;
			flex-direction: column;

			.back-btn {
				display: flex;
				align-items: center;
				gap: 2px;
				cursor: pointer;
			}

			.text {
				border-bottom: 1px solid #fff;
			}
		}
	}
</style>
